import React, { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';

interface RoleGuardProps {
  children?: ReactNode;
  roles: UserRole[];
  fallback?: ReactNode;
  redirectTo?: string;
}

/**
 * RoleGuard - Protects routes/content based on user roles
 * 
 * Usage as Route Element (with Outlet):
 * <Route element={<RoleGuard roles={['super_admin']} redirectTo="/dashboard" />}>
 *   <Route path="/admin/teams" element={<Teams />} />
 * </Route>
 * 
 * Usage as Component Wrapper:
 * <RoleGuard roles={['super_admin']}>
 *   <AdminOnlyContent />
 * </RoleGuard>
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({ 
  children, 
  roles, 
  fallback = null,
  redirectTo 
}) => {
  const { user, isLoading, hasRole } = useAuth();

  // Show nothing while loading
  if (isLoading) {
    return null;
  }

  // Not authenticated
  if (!user) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return <>{fallback}</>;
  }

  // Check if user has required role
  const hasAccess = hasRole(...roles);

  if (!hasAccess) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return <>{fallback}</>;
  }

  // If used as route element (no children), render Outlet for nested routes
  // If used as component wrapper (has children), render children
  return children ? <>{children}</> : <Outlet />;
};

/**
 * SuperAdminOnly - Convenience wrapper for super admin only content
 */
export const SuperAdminOnly: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <RoleGuard roles={['super_admin']} fallback={fallback}>
    {children}
  </RoleGuard>
);

/**
 * TeamAdminOnly - Convenience wrapper for team admin (and super admin) only content
 */
export const TeamAdminOnly: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <RoleGuard roles={['super_admin', 'team_admin']} fallback={fallback}>
    {children}
  </RoleGuard>
);

export default RoleGuard;
